import {
  isGoogleTagId,
  isMetaPixelId,
  normalizeGoogleTagId,
  STOREFRONT_TRACKING_CONFIGURATION_KEYS,
  type StorefrontBootstrap,
  type StorefrontTrackingConfiguration,
} from "@emynor/storefront-sdk";
import { useEffect } from "react";

type Gtag = (...args: unknown[]) => void;
type MetaPixel = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  push: MetaPixel;
  queue: unknown[][];
  loaded: boolean;
  version: string;
};

type TrackingWindow = Window & {
  dataLayer?: unknown[][];
  gtag?: Gtag;
  fbq?: MetaPixel;
  _fbq?: MetaPixel;
};

const initializedGoogleTags = new Set<string>();
const initializedMetaPixels = new Set<string>();
const lastGooglePage = new Map<string, string>();
const lastMetaPage = new Map<string, string>();

export function useStorefrontTracking(
  bootstrap: StorefrontBootstrap,
  pathname: string,
): void {
  const tracking = publishedStorefrontTracking(bootstrap);
  const googleTagId = tracking?.googleTagId ?? null;
  const metaPixelId = tracking?.metaPixelId ?? null;

  useEffect(() => {
    trackStorefrontPage({ googleTagId, metaPixelId }, pathname);
  }, [googleTagId, metaPixelId, pathname]);
}

export function trackPublishedStorefrontPage(
  bootstrap: StorefrontBootstrap,
  pathname: string,
): void {
  const tracking = publishedStorefrontTracking(bootstrap);
  if (tracking) trackStorefrontPage(tracking, pathname);
}

export function publishedStorefrontTracking(
  bootstrap: StorefrontBootstrap,
): StorefrontTrackingConfiguration | null {
  if (bootstrap.mode !== "published" || !bootstrap.storefront) return null;
  const configuration = bootstrap.storefront.configuration;
  const googleValue =
    configuration[STOREFRONT_TRACKING_CONFIGURATION_KEYS.googleTagId];
  const metaValue =
    configuration[STOREFRONT_TRACKING_CONFIGURATION_KEYS.metaPixelId];
  const googleTagId =
    typeof googleValue === "string" ? normalizeGoogleTagId(googleValue) : "";
  const metaPixelId = typeof metaValue === "string" ? metaValue.trim() : "";
  return {
    googleTagId: isGoogleTagId(googleTagId) ? googleTagId : null,
    metaPixelId: isMetaPixelId(metaPixelId) ? metaPixelId : null,
  };
}

function trackStorefrontPage(
  tracking: StorefrontTrackingConfiguration,
  pathname: string,
): void {
  if (pathname.endsWith("/payment-return")) return;
  if (tracking.googleTagId) {
    initializeGoogleTag(tracking.googleTagId);
    trackGooglePage(tracking.googleTagId, pathname);
  }
  if (tracking.metaPixelId) {
    initializeMetaPixel(tracking.metaPixelId);
    trackMetaPage(tracking.metaPixelId, pathname);
  }
}

function initializeGoogleTag(tagId: string): void {
  const trackingWindow = window as TrackingWindow;
  trackingWindow.dataLayer ??= [];
  trackingWindow.gtag ??= (...args: unknown[]) =>
    trackingWindow.dataLayer!.push(args);
  if (initializedGoogleTags.has(tagId)) return;

  appendScript(
    `emynor-google-tag-${tagId}`,
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tagId)}`,
  );
  trackingWindow.gtag("js", new Date());
  trackingWindow.gtag("config", tagId, { send_page_view: false });
  initializedGoogleTags.add(tagId);
}

function trackGooglePage(tagId: string, pathname: string): void {
  const trackingWindow = window as TrackingWindow;
  if (!trackingWindow.gtag || lastGooglePage.get(tagId) === pathname) return;
  trackingWindow.gtag("event", "page_view", {
    page_location: `${window.location.origin}${pathname}`,
    page_path: pathname,
    page_title: document.title,
    send_to: tagId,
  });
  lastGooglePage.set(tagId, pathname);
}

function initializeMetaPixel(pixelId: string): void {
  const trackingWindow = window as TrackingWindow;
  const fbq = trackingWindow.fbq ?? createMetaPixelQueue();
  trackingWindow.fbq = fbq;
  trackingWindow._fbq ??= fbq;
  if (initializedMetaPixels.has(pixelId)) return;

  appendScript(
    "emynor-meta-pixel",
    "https://connect.facebook.net/en_US/fbevents.js",
  );
  fbq("init", pixelId);
  initializedMetaPixels.add(pixelId);
}

function trackMetaPage(pixelId: string, pathname: string): void {
  const fbq = (window as TrackingWindow).fbq;
  if (!fbq || lastMetaPage.get(pixelId) === pathname) return;
  fbq("trackSingle", pixelId, "PageView");
  lastMetaPage.set(pixelId, pathname);
}

function createMetaPixelQueue(): MetaPixel {
  const queue = ((...args: unknown[]) => {
    if (queue.callMethod) queue.callMethod(...args);
    else queue.queue.push(args);
  }) as MetaPixel;
  queue.push = queue;
  queue.queue = [];
  queue.loaded = true;
  queue.version = "2.0";
  return queue;
}

function appendScript(id: string, src: string): void {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}
